import { useCallback, useEffect, useState } from "react";
import { 
  getRoles,
  createCustomRole,
  updateCustomRole,
  deleteCustomRole
} from "../../services/roles/roles.service";
import {
  syncRoles as syncRolesService
} from "../../services/sync/sync.service";

import { validate } from "../../utils";
import { useSnackbar } from "notistack";
import isEmpty from "lodash/isEmpty";
import { useDispatch } from "react-redux";
import { updateList, updateListMethod } from "../../store/actions/role.actions";
import { handlerErrorV2 } from "../../utils/errors";

const useRoles = () => {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchRoles = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getRoles();
      dispatch(updateList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createRole = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createCustomRole(form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateListMethod('POST', { ...response.data, isCustom: true }));
        enqueueSnackbar('Role created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateRole = async (roleId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateCustomRole(roleId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateListMethod('PUT', { ...response.data, isCustom: true }));
        enqueueSnackbar('Role updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteRole = async (roleId) => {
    try {
      const response = await deleteCustomRole(roleId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateListMethod('DELETE', roleId));
        enqueueSnackbar('Role deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const syncRole = async () => {
    try {
      const response = await syncRolesService();
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        enqueueSnackbar('Roles sync successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return { 
    isLoadingList, 
    errors, 
    createRole, 
    updateRole, 
    deleteRole,
    syncRole
  };
};

export default useRoles;